import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, CheckIcon, AlertTriangleIcon } from 'lucide-react';
// Transaction types grouped by their nature
const transactionTypeGroups = [{
  name: 'P2P Transactions',
  transactions: [{
    id: 'p2p_transfer',
    name: 'P2P Transfer'
  }, {
    id: 'account_identification',
    name: 'Account Identification'
  }, {
    id: 'alias_management',
    name: 'Alias Management'
  }]
}, {
  name: 'P2M Issuer Transactions',
  transactions: [{
    id: 'p2m_sqrc',
    name: 'P2M Payments - SQRC'
  }, {
    id: 'p2m_dqrc',
    name: 'P2M Payments - DQRC'
  }, {
    id: 'p2m_rtp_later',
    name: 'P2M Payments - RTP Later'
  }, {
    id: 'p2m_rtp_now',
    name: 'P2M Payments - RTP Now'
  }, {
    id: 'pisp_issuer',
    name: 'PISP - Issuer'
  }]
}, {
  name: 'P2M Acquirer Transactions',
  transactions: [{
    id: 'p2m_sqrc_acquirer',
    name: 'P2M Payments - SQRC - Acquirer'
  }, {
    id: 'p2m_dqrc_acquirer',
    name: 'P2M Payments - DQRC - Acquirer'
  }, {
    id: 'p2m_rtp_later_acquirer',
    name: 'P2M Payments - RTP Later - Acquirer'
  }, {
    id: 'p2m_rtp_now_acquirer',
    name: 'P2M Payments - RTP Now - Acquirer'
  }, {
    id: 'pisp_acquirer',
    name: 'PISP - Acquirer'
  }]
}, {
  name: 'Bulk Transactions',
  transactions: [{
    id: 'bulk_receiving',
    name: 'Bulk Receiving'
  }, {
    id: 'bulk_sending',
    name: 'Bulk Sending'
  }]
}];
// Sample participant data for editing
const sampleParticipant = {
  id: 'P001',
  name: 'National Bank of Ethiopia',
  shortName: 'NBE',
  bic: 'NBETETAA',
  status: 'Active',
  type: 'Bank',
  activationDate: '2023-01-15',
  enabledTransactions: ['account_identification', 'p2p_transfer', 'alias_management', 'p2m_sqrc', 'bulk_receiving']
};
interface EditParticipantProps {
  onNavigate?: (path: string) => void;
}
export function EditParticipant({
  onNavigate
}: EditParticipantProps = {}) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    shortName: '',
    bic: '',
    status: 'Active',
    type: 'Bank',
    activationDate: '',
    enabledTransactions: [] as string[]
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Simulate fetching data from API
  useEffect(() => {
    // Get participant ID from sessionStorage (set by the list page)
    const participantId = sessionStorage.getItem('editParticipantId');
    // Fallback to URL params if not in sessionStorage
    const urlParams = new URLSearchParams(window.location.hash.split('?')[1]);
    const urlParticipantId = urlParams.get('id');
    const idToUse = participantId || urlParticipantId;
    // Simulate API call with delay
    setTimeout(() => {
      if (idToUse) {
        // In a real app, you would fetch the specific participant
        // For now, we'll use the sample data and update the ID
        setFormData({
          ...sampleParticipant,
          id: idToUse
        });
      } else {
        setError('Participant ID is missing or invalid');
      }
      setLoading(false);
    }, 800);
  }, []);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleTransactionToggle = (transactionId: string) => {
    setFormData(prevData => {
      if (prevData.enabledTransactions.includes(transactionId)) {
        return {
          ...prevData,
          enabledTransactions: prevData.enabledTransactions.filter(id => id !== transactionId)
        };
      } else {
        return {
          ...prevData,
          enabledTransactions: [...prevData.enabledTransactions, transactionId]
        };
      }
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    // Here you would normally submit to your API
    console.log('Updated participant data:', formData);
    // Reset form submission state after a delay
    setTimeout(() => {
      setFormSubmitted(false);
    }, 3000);
  };
  // Navigate back to the participants list
  const handleBack = () => {
    if (onNavigate) {
      onNavigate('participants/list');
    } else {
      window.location.href = '#/participants/list';
    }
  };
  if (loading) {
    return <div className="bg-white rounded-lg shadow-sm p-6 flex justify-center items-center h-96" data-id="element-759">
        <div className="text-center" data-id="element-760">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F58220] mx-auto" data-id="element-761"></div>
          <p className="mt-4 text-gray-600" data-id="element-762">Loading participant data...</p>
        </div>
      </div>;
  }
  if (error) {
    return <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-763">
        <div className="flex items-center mb-6" data-id="element-764">
          <button className="mr-4 p-2 rounded-full hover:bg-gray-100" onClick={handleBack} data-id="element-765">
            <ArrowLeftIcon size={20} data-id="element-766" />
          </button>
          <h1 className="text-2xl font-semibold text-[#1A1F36]" data-id="element-767">
            Edit Participant
          </h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-md p-4" data-id="element-768">
          <div className="flex" data-id="element-769">
            <div className="flex-shrink-0" data-id="element-770">
              <AlertTriangleIcon className="h-5 w-5 text-red-400" data-id="element-771" />
            </div>
            <div className="ml-3" data-id="element-772">
              <p className="text-sm font-medium text-red-800" data-id="element-773">{error}</p>
              <p className="mt-2 text-sm text-red-700" data-id="element-774">
                Please try again or contact system administrator.
              </p>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-775">
      <div className="flex items-center mb-6" data-id="element-776">
        <button className="mr-4 p-2 rounded-full hover:bg-gray-100" onClick={handleBack} data-id="element-777">
          <ArrowLeftIcon size={20} data-id="element-778" />
        </button>
        <h1 className="text-2xl font-semibold text-[#1A1F36]" data-id="element-779">
          Edit Participant: {formData.name}
        </h1>
      </div>
      {formSubmitted ? <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6" data-id="element-780">
          <div className="flex" data-id="element-781">
            <div className="flex-shrink-0" data-id="element-782">
              <CheckIcon className="h-5 w-5 text-green-400" data-id="element-783" />
            </div>
            <div className="ml-3" data-id="element-784">
              <p className="text-sm font-medium text-green-800" data-id="element-785">
                Participant has been successfully updated and is pending
                approval.
              </p>
            </div>
          </div>
        </div> : <form onSubmit={handleSubmit} data-id="element-786">
          <div className="mb-6" data-id="element-787">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-788">
              Participant Code
            </label>
            <input type="text" value={formData.id} disabled className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-500" data-id="element-789" />
            <p className="mt-1 text-xs text-gray-500" data-id="element-790">
              Participant code is system generated and cannot be changed
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-791">
            <div data-id="element-792">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-793">
                Participant Name*
              </label>
              <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter full participant name" data-id="element-794" />
            </div>
            <div data-id="element-795">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-796">
                Short Name*
              </label>
              <input type="text" name="shortName" required value={formData.shortName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter short name (abbreviation)" maxLength={10} data-id="element-797" />
            </div>
            <div data-id="element-798">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-799">
                BIC*
              </label>
              <input type="text" name="bic" required value={formData.bic} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter BIC" maxLength={11} data-id="element-800" />
            </div>
            <div data-id="element-801">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-802">
                Status*
              </label>
              <select name="status" required value={formData.status} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-803">
                <option value="Active" data-id="element-804">Active</option>
                <option value="In-Active" data-id="element-805">In-Active</option>
                <option value="Closed" data-id="element-806">Closed</option>
              </select>
            </div>
            <div data-id="element-807">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-808">
                Participant Type*
              </label>
              <select name="type" required value={formData.type} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-809">
                <option value="Bank" data-id="element-810">Bank</option>
                <option value="MSP" data-id="element-811">MSP</option>
                <option value="Aggregator" data-id="element-812">Aggregator</option>
              </select>
            </div>
            <div data-id="element-813">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-814">
                Activation Date*
              </label>
              <input type="date" name="activationDate" required value={formData.activationDate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-815" />
            </div>
          </div>
          <div className="mb-8" data-id="element-816">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-817">
              Allowed Transactions
            </h2>
            {transactionTypeGroups.map(group => <div key={group.name} className="mb-6" data-id="element-818">
                <h3 className="text-md font-medium text-gray-800 mb-3 border-b pb-2" data-id="element-819">
                  {group.name}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-820">
                  {group.transactions.map(transaction => <div key={transaction.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50" data-id="element-821">
                      <input type="checkbox" id={transaction.id} checked={formData.enabledTransactions.includes(transaction.id)} onChange={() => handleTransactionToggle(transaction.id)} className="h-4 w-4 text-[#F58220] focus:ring-[#F58220] border-gray-300 rounded" data-id="element-822" />
                      <label htmlFor={transaction.id} className="text-sm text-gray-700 cursor-pointer" data-id="element-823">
                        {transaction.name}
                      </label>
                    </div>)}
                </div>
              </div>)}
          </div>
          <div className="border-t border-gray-200 pt-6 flex justify-end space-x-4" data-id="element-824">
            <button type="button" className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F58220]" onClick={handleBack} data-id="element-825">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F58220] hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F58220]" data-id="element-826">
              Submit for Approval
            </button>
          </div>
        </form>}
      <div className="mt-6 text-sm text-gray-500" data-id="element-827">
        <p data-id="element-828">* Required fields</p>
        <p data-id="element-829">
          Note: All participant modifications require approval from an
          authorized checker.
        </p>
      </div>
    </div>;
}